import type { AlbertMaterialView } from '@classgo/shared';

import type { RecentMaterial } from './types';

const LIMIT = 30;

const keyOf = (tenantId: string, userId: string) =>
  `classgo.albert.materials.${tenantId}.${userId}`;

const parse = (raw: string | null): RecentMaterial[] => {
  if (!raw) return [];
  try {
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value) ? (value as RecentMaterial[]) : [];
  } catch {
    return [];
  }
};

/**
 * The documents this person generated in this browser, newest first (#118). The API keeps every
 * document and re-issues its link by id, but lists none per topic; this local memory is what
 * lets the teacher find last week's worksheet again from the same computer. Never the link
 * itself (it expires): the id, and `GET /albert/materials/{id}` gives a fresh one.
 *
 * @param {Storage} storage -- `window.localStorage` (or a double).
 * @param {string} tenantId -- The school.
 * @param {string} userId -- The person, so two teachers on one computer never mix.
 * @returns The reads and the write.
 */
export const recentMaterials = (storage: Storage, tenantId: string, userId: string) => {
  const key = keyOf(tenantId, userId);
  const read = (): RecentMaterial[] => {
    try {
      return parse(storage.getItem(key));
    } catch {
      return [];
    }
  };
  return {
    /**
     * The documents of one topic, newest first.
     */
    ofTopic: (topicId: string): RecentMaterial[] =>
      read().filter((material) => material.topicId === topicId),
    /**
     * Remembers a document just generated (or replaces the entry with the same id) and returns
     * the topic's list.
     */
    remember: (material: AlbertMaterialView): RecentMaterial[] => {
      const entry: RecentMaterial = {
        id: material.id,
        topicId: material.topicId,
        kind: material.kind,
        level: material.level,
        title: material.title,
        filename: material.filename,
        createdAt: material.createdAt,
      };
      const next = [entry, ...read().filter((row) => row.id !== entry.id)].slice(0, LIMIT);
      try {
        storage.setItem(key, JSON.stringify(next));
      } catch {
        // Private mode or a full quota: the list lives in memory for this screen only.
      }
      return next.filter((row) => row.topicId === entry.topicId);
    },
  };
};
